import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Input, Badge } from "@learn-me/ui";
import { adminApi } from "@/features/admin/api/adminApi";
import {
  X,
  Building2,
  Mail,
  Phone,
  Users,
  Calendar,
  MessageSquare,
  Send,
} from "lucide-react";
import toast from "react-hot-toast";
import { handleApiError } from "@/lib/errorHandler";
import { formatDate } from "@learn-me/utils";

const STATUS_LABELS: Record<string, string> = {
  PENDING: "Pendiente",
  CONTACTED: "Contactado",
  CONVERTED: "Convertido",
  CLOSED: "Cerrado",
};

const STATUS_COLORS: Record<
  string,
  "default" | "success" | "warning" | "destructive"
> = {
  PENDING: "warning",
  CONTACTED: "default",
  CONVERTED: "success",
  CLOSED: "destructive",
};

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactId: string;
}

export function EnterpriseContactDetailModal({
  isOpen,
  onClose,
  contactId,
}: ModalProps) {
  const [newNote, setNewNote] = useState("");
  const queryClient = useQueryClient();

  const { data: contact, isLoading } = useQuery({
    queryKey: ["admin-enterprise-contact", contactId],
    queryFn: () => adminApi.getEnterpriseContact(contactId),
    enabled: isOpen,
  });

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const updateStatusMutation = useMutation({
    mutationFn: (status: "PENDING" | "CONTACTED" | "CONVERTED" | "CLOSED") =>
      adminApi.updateEnterpriseContactStatus(contactId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-enterprise-contact", contactId],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-enterprise-contacts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-enterprise-contacts-stats"],
      });
      toast.success("Estado actualizado");
    },
    onError: (error) => {
      handleApiError(error, "Error al actualizar el estado");
    },
  });

  const addNoteMutation = useMutation({
    mutationFn: (content: string) =>
      adminApi.addEnterpriseContactNote(contactId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-enterprise-contact", contactId],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-enterprise-contacts"],
      });
      setNewNote("");
      toast.success("Nota agregada");
    },
    onError: (error) => {
      handleApiError(error, "Error al agregar la nota");
    },
  });

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleAddNote = (e: React.FormEvent) => {
    e.preventDefault();
    if (newNote.trim()) {
      addNoteMutation.mutate(newNote.trim());
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={handleBackdropClick}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div className="relative z-10 w-full max-w-2xl mx-4 bg-white rounded-lg shadow-xl animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Detalle de Solicitud</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {isLoading ? (
            <div className="space-y-4 animate-pulse">
              <div className="h-6 bg-muted rounded w-1/2" />
              <div className="h-4 bg-muted rounded w-1/3" />
              <div className="h-20 bg-muted rounded" />
            </div>
          ) : contact ? (
            <>
              {/* Company Info */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">
                      {contact.companyName}
                    </h3>
                    {contact.employeeCountLabel && (
                      <p className="text-muted-foreground">
                        {contact.employeeCountLabel}
                      </p>
                    )}
                    {contact.offering && (
                      <p className="text-sm text-primary-700">
                        Solicitud sobre: {contact.offering.title}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>{contact.contactName}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a
                      href={`mailto:${contact.email}`}
                      className="text-primary hover:underline"
                    >
                      {contact.email}
                    </a>
                  </div>
                  {contact.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <a
                        href={`tel:${contact.phone}`}
                        className="text-primary hover:underline"
                      >
                        {contact.phone}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{formatDate(contact.createdAt)}</span>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <h4 className="font-medium">Mensaje</h4>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">
                    {contact.message}
                  </p>
                </div>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <h4 className="font-medium">Estado</h4>
                <div className="flex items-center gap-2">
                  <Badge variant={STATUS_COLORS[contact.status] || "default"}>
                    {STATUS_LABELS[contact.status] || contact.status}
                  </Badge>
                  <div className="flex gap-2 ml-auto">
                    {(
                      ["PENDING", "CONTACTED", "CONVERTED", "CLOSED"] as const
                    ).map((status) => (
                      <Button
                        key={status}
                        variant={
                          contact.status === status ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => updateStatusMutation.mutate(status)}
                        disabled={
                          updateStatusMutation.isPending ||
                          contact.status === status
                        }
                      >
                        {STATUS_LABELS[status]}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Notas ({contact.notes.length})
                </h4>

                {contact.notes.length > 0 && (
                  <div className="space-y-3">
                    {contact.notes.map((note) => (
                      <div
                        key={note.id}
                        className="p-3 bg-muted/50 rounded-lg border"
                      >
                        <p className="text-sm whitespace-pre-wrap mb-2">
                          {note.content}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {note.admin.firstName} {note.admin.lastName} •{" "}
                          {formatDate(note.createdAt)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                <form onSubmit={handleAddNote} className="flex gap-2">
                  <Input
                    placeholder="Agregar una nota..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="submit"
                    disabled={!newNote.trim() || addNoteMutation.isPending}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No se pudo cargar la información.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}
